import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Calendar, Clock, CreditCard, Video, Loader2, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { startPayfastCheckout } from "@/lib/payments";
import { Button, Avatar, Tag, Loading, EmptyState } from "@/components/brand";
import { PageHeader } from "@/components/PageHeader";
import { canJoin, formatDateLong, formatR, formatTime, isPast, minutesUntil } from "@/lib/format";

export default function BookingDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const confirmed = sp.get("confirmed");
  const pf = sp.get("pf");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const b = useQuery({
    queryKey: ["booking", id],
    queryFn: () => api(`/bookings/${id}`),
    refetchInterval: (q) => (q.state.data?.status === "pending_payment" ? 3000 : false),
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["bookings"] }); qc.invalidateQueries({ queryKey: ["booking", id] }); qc.invalidateQueries({ queryKey: ["classes"] }); };

  const retryPay = useMutation({ mutationFn: () => startPayfastCheckout(id, `/booking/${id}?pf=1`), onError: (e) => toast.error(e.message) });
  const abandon = useMutation({ mutationFn: () => api(`/payments/${id}/abandon`, { method: "POST" }), onSuccess: () => { invalidate(); toast.message("Booking cancelled — nothing was charged."); }, onError: (e) => toast.error(e.message) });
  const cancel = useMutation({ mutationFn: () => api(`/bookings/${id}/cancel`, { method: "POST" }), onSuccess: (r) => { invalidate(); toast[r.refunded ? "success" : "message"](r.refunded ? "Cancelled — full refund issued." : "Cancelled. Inside the cancellation window, so no refund applies."); }, onError: (e) => toast.error(e.message) });
  const complete = useMutation({ mutationFn: () => api(`/bookings/${id}/complete`, { method: "POST" }), onSuccess: () => { invalidate(); navigate(`/complete/${id}`); } });

  if (b.isLoading) return <Loading />;
  if (!b.data) return <><PageHeader /><EmptyState title="Booking not found" /></>;
  const d = b.data;
  const joinable = d.status === "confirmed" && canJoin(d.start_time, d.duration_min);
  const past = isPast(d.start_time, d.duration_min);
  const mins = minutesUntil(d.start_time);
  const window = d.type === "class" ? 12 : 24;
  const isMember = user?.id === d.user_id;
  const pending = d.status === "pending_payment";
  const showConfirmed = (confirmed || pf) && d.status === "confirmed";

  const payText = pending ? `${formatR(d.amount_paid ?? d.price)} due · PayFast`
    : d.status === "refunded" ? `${formatR(d.price)} refunded`
    : d.payment_provider === "fitpass" ? "Paid with Fit Pass credit"
    : d.discount ? `${formatR(d.amount_paid)} paid · ${d.points_used} pts saved ${formatR(d.discount)}`
    : `${formatR(d.price)} paid · ref ${d.id.slice(-6).toUpperCase()}`;

  return (
    <div data-testid="booking-detail-screen" className="max-w-2xl">
      <PageHeader title={showConfirmed || pending ? "" : "Booking"} onBack={() => (confirmed || pf ? navigate("/bookings") : navigate(-1))} />

      {pending ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center" data-testid="booking-pending-banner">
          <div className="w-[72px] h-[72px] rounded-full bg-ink flex items-center justify-center"><Loader2 className="animate-spin text-white" size={32} /></div>
          <h2 className="font-display text-3xl text-ink tracking-tight">Awaiting Payment</h2>
          <p className="font-body text-mutedgrey max-w-sm">{pf ? "Confirming your PayFast payment… this usually takes a few seconds." : "Complete your PayFast payment to secure this spot. Your spot is held for 30 minutes."}</p>
        </div>
      ) : showConfirmed ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center" data-testid="booking-confirmed-banner">
          <div className="w-[72px] h-[72px] rounded-full bg-brand flex items-center justify-center"><Check size={32} className="text-white" /></div>
          <h2 className="font-display text-3xl text-ink tracking-tight">Booking Confirmed</h2>
          <p className="font-body text-mutedgrey">Your live room will open 10 minutes before the session.</p>
        </div>
      ) : null}

      <div className="p-5 rounded-xl2 bg-brand-soft space-y-3">
        <Tag label={d.type === "private" ? "Private Session" : "Live Group Class"} tone={d.type === "private" ? "dark" : "pink"} />
        <h1 className="font-display text-2xl text-ink tracking-tight" data-testid="booking-title">{d.title}</h1>
        <div className="flex items-center gap-2"><Avatar uri={d.trainer_avatar} size={40} name={d.trainer_name} /><span className="font-body font-semibold text-ink">with {d.trainer_name}</span></div>
        <div className="h-px bg-brand/20" />
        {[[Calendar, formatDateLong(d.start_time)], [Clock, `${formatTime(d.start_time)} · ${d.duration_min} minutes (your local time)`], [CreditCard, payText]].map(([Icon, v], i) => (
          <div key={i} className="flex items-center gap-3"><Icon size={18} className="text-brand" /><span className="font-body text-[15px] text-ink">{v}</span></div>
        ))}
        <div className="h-px bg-brand/20" />
        <div className="flex items-center gap-2"><span className="font-body font-semibold text-[13px] text-mutedgrey">Status</span><Tag label={pending ? "awaiting payment" : d.status} tone={d.status === "completed" ? "green" : d.status === "confirmed" ? "pink" : pending ? "dark" : "grey"} /></div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {pending && isMember ? (
          <>
            <Button testID="booking-pay-now-button" title="Pay with PayFast" loading={retryPay.isPending} onClick={() => retryPay.mutate()} full />
            <Button testID="booking-abandon-button" title="Cancel — don't book" variant="outline" loading={abandon.isPending} onClick={() => abandon.mutate()} full />
          </>
        ) : null}

        {d.status === "confirmed" && !past ? (
          joinable ? <Button testID="booking-join-button" title={d.type === "private" ? "Join Private Room" : "Join Live Class"} icon={Video} onClick={() => navigate(`/live/${d.id}`)} full />
          : <div className="flex items-center gap-3 p-4 rounded-md bg-surface2" data-testid="booking-countdown"><Clock size={18} className="text-brand" /><span className="font-body font-semibold text-ink">Starts in {mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`}. Join opens 10 minutes before.</span></div>
        ) : null}

        {d.status === "confirmed" && !past && isMember ? (
          <>
            <Button testID="booking-cancel-button" title="Cancel Booking" variant="outline" loading={cancel.isPending} onClick={() => cancel.mutate()} full />
            <p className="font-body text-xs text-mutedgrey text-center">Cancel more than {window} hours ahead for a full refund. Inside {window} hours the booking is non-refundable.</p>
          </>
        ) : null}

        {d.status === "confirmed" && past && isMember ? <Button testID="booking-mark-complete" title="Mark as attended" variant="dark" onClick={() => complete.mutate()} full /> : null}
        {d.status === "completed" && isMember && !d.reviewed ? <Button testID="booking-review-button" title={`Rate ${d.trainer_name.split(" ")[0]}`} icon={Star} variant="ghost" onClick={() => navigate(`/complete/${d.id}`)} full /> : null}
        <Button testID="booking-view-trainer" title="View Trainer Profile" variant="ghost" onClick={() => navigate(`/trainers/${d.trainer_id}`)} full />
      </div>
    </div>
  );
}
